import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';
import { toast } from "@/hooks/use-toast";
import { Calendar, Clock } from 'lucide-react';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
}

interface AttendanceConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: TrainingSession | null;
}

const AttendanceConfirmationDialog: React.FC<AttendanceConfirmationDialogProps> = ({
  open,
  onOpenChange,
  session
}) => {
  useUiLanguage();
  const { t } = useTranslation(['admin', 'common']);
  const { profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleAttendanceResponse = async (willAttend: boolean) => {
    if (!session || !profile) return;

    setIsLoading(true);

    try {
      // Check if response already exists
      const { data: existingResponse } = await supabase
        .from('session_attendance')
        .select('id')
        .eq('session_id', session.id)
        .eq('student_id', profile.id)
        .single();

      if (existingResponse) {
        // Update existing response
        const { error } = await supabase
          .from('session_attendance')
          .update({ attended: willAttend })
          .eq('id', existingResponse.id);

        if (error) throw error;
      } else {
        // Create new response
        const { error } = await supabase
          .from('session_attendance')
          .insert({
            session_id: session.id,
            student_id: profile.id,
            attended: willAttend
          });

        if (error) throw error;
      }

      toast({
        title: t('admin:attendance.responseRecorded'),
        description: t('admin:attendance.responseRecordedDescription', {
          response: willAttend ? t('admin:attendance.willAttendResponse') : t('admin:attendance.wontAttendResponse')
        })
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error recording attendance response:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to record your response. Please try again."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timeString: string) => {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString(currentLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('admin:attendance.confirmationTitle')}</DialogTitle>
          <DialogDescription>
            {t('admin:attendance.confirmationDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 border rounded-lg bg-gray-50">
            <h4 className="font-semibold text-gray-900 mb-2">{session.title}</h4>

            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(session.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{formatTime(session.start_time)} - {formatTime(session.end_time)}</span>
              </div>
              {session.description && (
                <p className="mt-2 text-gray-700">{session.description}</p>
              )}
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600 mb-4">
              {t('admin:attendance.willYouAttend')}
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleAttendanceResponse(false)}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? t('admin:attendance.recording') : t('admin:attendance.cannotAttend')}
          </Button>
          <Button
            onClick={() => handleAttendanceResponse(true)}
            disabled={isLoading}
            className="bg-red-900 hover:bg-red-800 flex-1"
          >
            {isLoading ? t('admin:attendance.recording') : t('admin:attendance.willAttend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceConfirmationDialog;
